/**
 * Leaders — the face of a kingdom, and the second of the two passive layers.
 *
 * ── What a leader is, and what it is not ────────────────────────────────────
 *
 * A leader is *not* a twelfth hero class. The hero system already exists and is
 * good — five archetypes, levels, skill points, a small tree, passives folded
 * into `w.mods` like everything else — and duplicating it per kingdom would give
 * us twenty-two skill trees to balance and nobody to balance them. So a leader
 * **names** an archetype and inherits the whole of it, and adds on top:
 *
 *   • two passives that are this person's alone, and
 *   • a `HouseLook`, which is the culture the race deliberately does not carry.
 *
 * Choosing a kingdom fixes the race *and* the leader, in that order, and the
 * leader is why two kingdoms of one race are two different games rather than one
 * game in two colours.
 *
 * ── Why the culture lives here and not on the race ──────────────────────────
 *
 * Because it is the only place it can live without breaking the thing the owner
 * asked for twice. `RaceFace` is bone and every kingdom of a people shares it
 * exactly; if the goblins' *race* entry carried "scrap iron and rag", the second
 * goblin kingdom would have nothing left to be but a different green. Everything
 * worn, cut, forged or held is a `HouseLook` field, and `housesDiffer` counts how
 * many of the five identity levers two houses actually use. Three is the floor
 * and `tests/races.test.ts` enforces it over every pair in this file, because a
 * rule nobody checks is a rule that erodes.
 *
 * ── The two goblin houses, since they are the test ──────────────────────────
 *
 * Loudest Gribb is bare-headed, unarmoured, in rag, chin up, mouth open, missing
 * a tusk. Nash Ninefingers is under a horned helm, scalp shaved and marked, in
 * bone and pelt, chin down, mouth shut. The same skull to the millimetre, and no
 * shared read at any size. That is what the two layers buy.
 *
 * ── The seam to `mapsea` ────────────────────────────────────────────────────
 *
 * This file generates no kingdoms. `mapsea` seats several rival kingdoms per
 * region; for each one it needs a race and a leader, and `leadersOfRace` and
 * `leaderForKingdom` are how it gets them without an RNG of its own.
 *
 * ── Why seven races have three houses and not two ───────────────────────────
 *
 * Two was "exactly enough for a region to hold two kingdoms of one people that
 * hate each other", and it was, right up until a region held *three*.
 *
 * `leaderForKingdom` is indexed by how many crowns of that people have been
 * seated anywhere and it wraps. With two houses and three crowns of one race in
 * one region the arithmetic is forced: the third crown gets the first crown's
 * hall, and two neighbours in the same neighbourhood answer to the same person
 * — which is the one thing this whole file exists to prevent. It reads as a
 * duplicate rather than as a rivalry, and no amount of work on the two faces
 * fixes it, because there are only two faces.
 *
 * The eastern continent made it the common case rather than the odd one:
 * Karrun-Dûr now hold ten crowns across four ranges, the Long Noon nine across
 * three rain coasts, the Thirstless nine across the sand. So every people that
 * seats three crowns in any one region has a third house — Karrun-Dûr, the
 * Verdant Choir, the Moonbound, the Drowned Court, the Splitmoot, the Long Noon
 * and the Thirstless. The rule to keep is not "three": it is **a people needs
 * at least as many houses as the busiest region seats crowns of it**, and if a
 * region ever seats four, this file needs a fourth.
 *
 * `housesDiffer` is pairwise, so a third house has to clear three against
 * *both* of the others rather than one, and every third below is built to clear
 * five.
 */

import type { HouseLook } from './faces.ts';
import type { HeroArchetype } from './heroes.ts';
import { race, RACES, type RaceId } from './races.ts';

/**
 * A leader passive. Layer two of two, sitting *on top of* the race passives
 * rather than replacing any of them.
 *
 * Same bag, same rules and the same `structural` escape hatch as `RacePassive` —
 * see the note there. Written as the value you would write on a tech node,
 * because `src/data` may not import `defaultMods()` and therefore cannot know
 * which keys multiply and which add.
 */
export interface LeaderPassive {
  id: string;
  name: string;
  rules: string;
  mods?: Record<string, number>;
  /** Named system, for a passive that is a shape rather than a number. */
  structural?: string;
}

export interface Leader {
  id: string;
  /** The name under the portrait. */
  name: string;
  /** What comes before it — Marshal, Grudgekeeper, Loudest. */
  title: string;
  raceId: RaceId;
  epithet: string;
  flavour: string;
  /**
   * The hero class this leader fights and levels as. The existing tree, whole
   * and unmodified; the passives below are the only thing that is theirs.
   */
  archetype: HeroArchetype;
  /** Layer two. Two apiece — a third is a build, not a character. */
  passives: readonly LeaderPassive[];
  /** The culture. Everything the race is forbidden to carry. */
  look: HouseLook;
}

/** `crown, grooming, beard, material, mantle, tilt, gaze, mouth, mark, age`. */
type LookSpec = [
  HouseLook['crown'],
  HouseLook['grooming'],
  HouseLook['beard'],
  HouseLook['material'],
  HouseLook['mantle'],
  number,
  HouseLook['bearing']['gaze'],
  number,
  HouseLook['mark'],
  number,
];

function L(
  id: string,
  raceId: RaceId,
  title: string,
  name: string,
  archetype: HeroArchetype,
  epithet: string,
  flavour: string,
  accent: number,
  accentDark: number,
  s: LookSpec,
  passives: readonly LeaderPassive[],
): Leader {
  return {
    id,
    name,
    title,
    raceId,
    epithet,
    flavour,
    archetype,
    passives,
    look: {
      id,
      race: raceId,
      leader: `${title} ${name}`,
      accent,
      accentDark,
      crown: s[0],
      grooming: s[1],
      beard: s[2],
      material: s[3],
      mantle: s[4],
      bearing: { tilt: s[5], gaze: s[6], mouth: s[7] },
      mark: s[8],
      age: s[9],
    },
  };
}

function p(id: string, name: string, rules: string, mods?: Record<string, number>, structural?: string): LeaderPassive {
  return structural ? { id, name, rules, mods, structural } : { id, name, rules, mods };
}

export const LEADERS: readonly Leader[] = [
  // ── The Ashen Crown ──────────────────────────────────────────────────────
  L(
    'ashen_vane', 'ashen', 'Marshal', 'Orsel Vane', 'warlord',
    'holds the line and the ledger, and resents one of them',
    'Signed for the army eleven years ago and has been trying to hand it back ever since. Nobody will take the pen.',
    0xd0a83c, 0x8a6a18,
    ['helm-open', 'cropped', 'stubble', 'steel', 'gorget', -3, 'narrow', -1, 'scar', 0.55],
    [
      p('vane_standing', 'The Standing Order', 'Companies will not rout above a third strength, and reinforcement costs a third less.', { moraleFloor: 0.32, reinforceCost: 0.66 }),
      p('vane_late', 'Paid Late, Paid Eventually', 'Arrears accrue at half rate. They have been here before and they will give you another fortnight.', undefined, 'systems/settlement.ts — the arrears clock'),
    ],
  ),
  L(
    'ashen_loam', 'ashen', 'Chancellor', 'Hesper Loam', 'castellan',
    'has never seen the frontier and has costed every mile of it',
    'Believes the empire failed for want of a second clerk. Has hired eleven, and is not yet ready to revise the figure.',
    0xc8b27a, 0x7a6a3a,
    ['bare', 'long', 'none', 'gold', 'robe', 4, 'aside', 0, 'none', 0.7],
    [
      p('loam_writ', 'By Writ', 'Buildings cost a further 12% less and the debt floor is 700 rather than 400.', { buildCost: 0.88 }, 'systems/settlement.ts — the race-conditional gold floor'),
      p('loam_assessment', 'Assessment', 'Tax yields 15% more at every notch of the dial, and the people mind 15% more as well.', { }, 'systems/settlement.ts — taxProjection and moraleTarget'),
    ],
  ),

  // ── The Gilded Vigil ─────────────────────────────────────────────────────
  L(
    'vigil_ost', 'vigil', 'Lampbearer', 'Calumn Ost', 'prelate',
    'carries the light and will not say for whom',
    'Has read the ninth hundred oaths aloud once a year for thirty years. Has never once been asked what is in them.',
    0xe8c477, 0x9a7429,
    ['mitre', 'tonsure', 'none', 'gold', 'chasuble', 6, 'shut', 1, 'burn', 0.6],
    [
      p('ost_naming', 'The Naming', 'Consecrated names are made half again as fast, and one is held in reserve.', undefined, 'systems/companies.ts — the names binding'),
      p('ost_lamplight', 'Lamplight', 'Powers cost 20% less Will and Will regenerates 25% faster.', { willRegen: 1.25, powerCooldown: 0.85 }),
    ],
  ),
  L(
    'vigil_vaul', 'vigil', 'Confessor', 'Irenne Vaul', 'sorcerer',
    'hears everything and forgives at her own pace',
    'Keeps the register of who has been forgiven. It is shorter than the other one and she is not hurrying.',
    0xbfd6e0, 0x5f7a8a,
    ['veil', 'shaved', 'none', 'glass', 'robe', -2, 'open', -2, 'brand', 0.35],
    [
      p('vaul_severity', 'Severity', 'Powers deal 35% more damage and reach 20% further.', { powerDamage: 1.35, powerRadius: 1.2 }),
      p('vaul_ledger', 'The Shorter Register', 'A company whose consecrated name is lost may be re-named once, free.', undefined, 'systems/companies.ts — the names binding'),
    ],
  ),

  // ── Karrun-Dûr ───────────────────────────────────────────────────────────
  L(
    'karrun_umm', 'karrun', 'Vaultwarden', 'Borvi Umm', 'castellan',
    'has not been above ground since the year of the flood',
    'Knows the hold by sound. Can tell you which gallery you are standing in from a cough.',
    0xc06a2c, 0x7a3d15,
    ['helm-closed', 'braided', 'plaited', 'iron', 'pauldrons', -4, 'narrow', -2, 'none', 0.8],
    [
      p('umm_coursing', 'Coursing', 'Walls have a further 40% health and siege damage to your structures is a third less.', { wallHp: 1.4, siegeResist: 0.66 }),
      p('umm_deeper', 'One Gallery Deeper', 'Delvings hold a third more, and stone and iron come 20% faster.', { resourceRate: 1.2 }),
    ],
  ),
  L(
    'karrun_bral', 'karrun', 'Grudgekeeper', 'Hessa Bral', 'warlord',
    'writes it down before the dust settles',
    'Carries the Book because nobody else would. Has begun writing in it about the people who would not.',
    0x9a6a48, 0x4e3520,
    ['circlet', 'shaved-marked', 'forked', 'stone', 'scale', 2, 'aside', 0, 'tattoo', 0.45],
    [
      p('bral_reckoning', 'The Reckoning', 'Every loss writes two Grudges rather than one. You will be compensated for this eventually.', undefined, 'systems/settlement.ts — the grudge ledger'),
      p('bral_settled', 'Settled In Full', 'Your units deal 20% more damage to whatever last destroyed a building of yours.', { unitDamage: 1.08 }, 'systems/combat.ts — the grudge target'),
    ],
  ),
  L(
    'karrun_thane', 'karrun', 'Roadwarden', 'Dagra Thane', 'prelate',
    'holds the surface road, which is nobody else’s idea of a promotion',
    'Was given the daylight galleries as a punishment and has spent forty years making them worth wanting. The hold is beginning to suspect it lost that exchange.',
    0xb08a2a, 0x6a5010,
    ['mask', 'tonsure', 'long', 'bronze', 'robe', 6, 'shut', 1, 'burn', 0.6],
    [
      p('thane_road', 'The Surface Road', 'Everything hauled overland arrives 25% faster, and your carters cannot be intercepted on your own ground.', { resourceRate: 1.12 }),
      p('thane_daylight', 'Daylight Work', 'Buildings go up 20% faster and repairs cost half. The hold disapproves and uses the roads anyway.', { buildSpeed: 1.2, buildCost: 0.9 }),
    ],
  ),

  // ── The Verdant Choir ────────────────────────────────────────────────────
  L(
    'verdant_ailu', 'verdant', 'Thornwright', 'Ailu of the Ninth Ring', 'huntmaster',
    'younger than most of what she commands',
    'The ninth ring was planted the year she was born, which makes her the youngest thing in the grove and its senior officer.',
    0x8fb04a, 0x4d6320,
    ['wreath', 'wild', 'none', 'wood', 'pelt', 3, 'open', 0, 'tattoo', 0.3],
    [
      p('ailu_quick', 'Quickwood', 'Groves mature 30% faster and Hardfrost takes half as many.', undefined, 'systems/settlement.ts — the groves binding'),
      p('ailu_loosed', 'Loosed Early', 'Archers gain 2 tiles of range and 15% damage.', { archerRange: 2, unitDamage: 1.15 }),
    ],
  ),
  L(
    'verdant_sorrel', 'verdant', 'Grovemother', 'Sorrel Ash', 'prelate',
    'remembers the wood before it was asked for anything',
    'Has outlived four of her own groves. Speaks of them the way other people speak of children, and with the same care about tense.',
    0x6e8a56, 0x36462a,
    ['antler-rack', 'braided', 'none', 'bone', 'robe', -6, 'shut', -1, 'none', 0.85],
    [
      p('sorrel_deeproot', 'Deeproot', 'Groves compound twice as hard, and a felled grove leaves its heads behind.', undefined, 'systems/settlement.ts — the groves binding'),
      p('sorrel_hardfrost', 'Against Hardfrost', 'Winter yields are doubled and the town eats 20% less in it.', { grainUpkeep: 0.85 }),
    ],
  ),
  L(
    'verdant_holt', 'verdant', 'Coppicer', 'Iyen Holt', 'huntmaster',
    'cuts the grove on purpose and is not popular for it',
    'Argues that a wood which is never cut is a wood that dies standing. Has been right for two hundred years and forgiven for none of them.',
    0x6f8f3a, 0x39501a,
    ['coif', 'topknot', 'short', 'lacquer', 'harness', 0, 'aside', -1, 'scar', 0.5],
    [
      p('holt_coppice', 'Coppicing', 'A grove felled on purpose returns its heads to the next ring and matures it at once. Hardfrost takes a quarter fewer.', undefined, 'systems/settlement.ts — the groves binding'),
      p('holt_thicket', 'Thicketwork', 'Enemies on your ground take 12% more damage and move a fifth slower.', { thorns: 0.12 }),
    ],
  ),

  // ── The Silver Reaches ───────────────────────────────────────────────────
  L(
    'silver_solenne', 'silver', 'Archivist', 'Aeryth Solenne', 'sorcerer',
    'is cataloguing the realm and expects to finish',
    'Has indexed every art the Reaches ever had. Eleven of them now exist only in the index, and she does not discuss which.',
    0xa8c4d8, 0x4d7290,
    ['circlet', 'long', 'none', 'glass', 'robe', 5, 'aside', -1, 'none', 0.5],
    [
      p('solenne_recall', 'Recall', 'Powers cool 30% faster and maximum Will is +60.', { powerCooldown: 0.7, willMax: 60 }),
      p('solenne_index', 'The Index', 'Academy research costs 25% less. It has all been done once already.', undefined, 'apply.ts — doResearch, the Academy gate'),
    ],
  ),
  L(
    'silver_icefall', 'silver', 'Warden', 'Tamber Icefall', 'huntmaster',
    'counts what is left every morning',
    'Lost the eye at Widow Pass and the company with it. Has not raised another and will not be drawn on the subject.',
    0x8ea0aa, 0x475660,
    ['helm-open', 'topknot', 'none', 'steel', 'scale', -3, 'narrow', -2, 'blind-eye', 0.65],
    [
      p('icefall_husbandry', 'Husbandry', 'Your units take 25% less damage from ranged attacks and move 15% faster.', { moveSpeed: 1.15, unitHp: 1.15 }),
      p('icefall_whole', 'Raised Whole', 'A company raised at full strength costs 20% less. It is the only kind you may raise.', undefined, 'systems/companies.ts — the irreplaceable binding'),
    ],
  ),

  // ── The Drowned Court ────────────────────────────────────────────────────
  L(
    'drowned_quill', 'drowned', 'Corsair-Prince', 'Maleth Quill', 'warlord',
    'is home twice a year and resents both visits',
    'Has never lost a ship, by the definition of loss he uses, which excludes ships he meant to leave somewhere.',
    0x4a9cb8, 0x22505f,
    ['mask', 'cropped', 'none', 'coral', 'harness', -2, 'narrow', -1, 'scar', 0.4],
    [
      p('quill_taken', 'Taken Alive', 'Thralls are taken from below a third of health rather than a fifth, and arrive unhurt.', undefined, 'systems/combat.ts — the capture path'),
      p('quill_shore', 'Off The Shore', 'Your units are 20% faster and deal 15% more damage away from your own walls.', { moveSpeed: 1.2, unitDamage: 1.1 }),
    ],
  ),
  L(
    'drowned_wrack', 'drowned', 'Tidejudge', 'Ysolde Wrack', 'prelate',
    'sentences the sea and is usually upheld',
    'The Court sits below the waterline. She has explained the arrangement to three visitors and buried all three.',
    0x2f6a86, 0x143848,
    // `veil` rather than `hood`, at the portrait branch's request and for a
    // reason worth keeping: `coif`, `hood` and `veil` are three shapes for one
    // read — cloth enclosing the head — and `hood` is specifically the shape
    // this repository has had to take off the Huntmaster twice for reading as a
    // cowl at thumbnail size. A veil hangs loose past the jaw instead of
    // enclosing, which is both drawable and the better fit for a judge who does
    // not look at what she is sentencing.
    ['veil', 'long', 'none', 'bone', 'robe', 6, 'blind', 0, 'tattoo', 0.75],
    [
      p('wrack_reaping', 'The Deep Reaping', 'Souls from every source are worth 60% more, and thralls yield Ichor as they work.', { soulGain: 1.6 }),
      p('wrack_fog', 'Judgment Fog', 'Enemies within your walls lose a fifth of their damage and half their morale.', { }, 'systems/combat.ts — the madness field'),
    ],
  ),
  L(
    'drowned_serrow', 'drowned', 'Pensmaster', 'Ilv Serrow', 'castellan',
    'never sails, and owns most of what comes back',
    'Runs the pens and the ledger of the pens. Has never seen the open sea and can tell you the tonnage of every hull that ever left the harbour.',
    0x7fb0a0, 0x2f5a50,
    ['helm-open', 'braided', 'none', 'lacquer', 'pelt', 4, 'open', 1, 'brand', 0.65],
    [
      p('serrow_pens', 'The Pens', 'Thralls keep half again as long and every one taken is worth a second head in labour.', undefined, 'systems/companies.ts — the thralls binding'),
      p('serrow_harbour', 'Harbour Work', 'Walls and buildings on a coast have 30% more health, and your ground yields 20% more Souls.', { soulGain: 1.2 }),
    ],
  ),

  // ── The Crimson Assize ───────────────────────────────────────────────────
  L(
    'crimson_amaranth', 'crimson', 'Justiciar', 'Vessine Amaranth', 'prelate',
    'keeps the schedule and has never missed a sitting',
    'Insists the arrangement is consensual and produces, on request, four hundred signatures. All of them are hers.',
    0xa8323c, 0x5e161c,
    ['crown-heavy', 'long', 'none', 'gold', 'chasuble', 5, 'open', 1, 'none', 0.55],
    [
      p('amaranth_orderly', 'Orderly', 'Ichor upkeep is a third lower and a thirsty company takes twice as long to turn.', undefined, 'systems/settlement.ts — the thirst clock'),
      p('amaranth_sitting', 'The Sitting', 'Your units heal for a further 8% of damage dealt and 15% faster attack.', { lifesteal: 0.08, attackSpeed: 1.15 }),
    ],
  ),
  L(
    'crimson_sen', 'crimson', 'The Nightclerk', 'Ordo Sen', 'castellan',
    'does the arithmetic nobody wants read aloud',
    'Works out how many the winter can spare. Has been right eleven years running, which is not the same as forgiven.',
    0x6a2a3a, 0x33121c,
    ['coif', 'shaved', 'moustache', 'lacquer', 'gorget', -4, 'shut', -2, 'burn', 0.35],
    [
      p('sen_measure', 'To The Measure', 'A head yields half again as much Ichor, and the town knows it is being counted.', undefined, 'systems/settlement.ts — the thirst clock'),
      p('sen_hours', 'The Small Hours', 'Buildings cost 15% less and finish 20% faster; the work is done at night.', { buildCost: 0.85, buildSpeed: 1.2 }),
    ],
  ),

  // ── The Splitmoot ────────────────────────────────────────────────────────
  //
  // The pair the whole two-layer design exists for. Read their two `LookSpec`
  // rows against each other: nothing is shared but the skull.
  L(
    'splitmoot_gribb', 'splitmoot', 'Loudest', 'Gribb', 'warlord',
    'is loudest, which is the whole constitutional position',
    'Won the argument. Cannot remember what it was about and is not going to be the one who asks.',
    0x8fae3e, 0x4a5c1c,
    ['bare', 'wild', 'none', 'rag', 'bare', 7, 'open', 2, 'broken-tusk', 0.25],
    [
      p('gribb_more', 'More Of Us', 'The warren breeds 40% faster and tolerates a third more press before it turns.', undefined, 'systems/settlement.ts — the moot binding'),
      p('gribb_forward', 'Forward, Mostly', 'Your units are 20% faster and rout 30% later than they should.', { moveSpeed: 1.2, moraleFloor: 0.12 }),
    ],
  ),
  L(
    'splitmoot_nash', 'splitmoot', 'Second-Loudest', 'Nash Ninefingers', 'huntmaster',
    'has been second-loudest for nine years and is patient',
    'Keeps a list. It is not written down, because he cannot, but he has never needed it explained back to him.',
    0x6f7a2c, 0x353c14,
    ['helm-horned', 'shaved-marked', 'none', 'bone', 'pelt', -5, 'narrow', -2, 'warpaint', 0.6],
    [
      p('nash_thinning', 'A Useful Thinning', 'The mob damages your buildings half as hard, and every goblin it takes is worth 3 Scrap.', undefined, 'systems/settlement.ts — the moot binding'),
      p('nash_volley', 'Everything At Once', 'Companies hold 30% more and throw whatever they are holding.', { popCap: 1.3, unitDamage: 1.12 }),
    ],
  ),
  L(
    'splitmoot_umber', 'splitmoot', 'Third-Loudest', 'Umber Quiet', 'castellan',
    'has never once raised his voice and is somehow still in the room',
    'Nobody can recall electing him and nobody can recall a motion he did not end up drafting. The Splitmoot regards this as a coincidence of long standing.',
    0x9caa66, 0x4e5828,
    ['coif', 'topknot', 'none', 'iron', 'robe', 1, 'shut', 0, 'tattoo', 0.55],
    [
      p('umber_quiet', 'A Quiet Word', 'The press turns 40% later and damages your buildings a third as hard.', undefined, 'systems/settlement.ts — the moot binding'),
      p('umber_drafted', 'Drafted In Advance', 'Buildings cost 15% less and walls go up at half price. Somebody had already written it down.', { buildCost: 0.85 }),
    ],
  ),

  // ── The Cog-Warrens ──────────────────────────────────────────────────────
  L(
    'cogwarren_dremm', 'cogwarren', 'Overseer', 'Skiff Dremm', 'castellan',
    'signs off on the crew lists and does not read them',
    'Has run the works for six years without an accident, by a definition of accident agreed with himself.',
    0xb8862c, 0x6a4a12,
    ['helm-closed', 'bald', 'none', 'iron', 'harness', -6, 'blind', 0, 'burn', 0.5],
    [
      p('dremm_crewing', 'Crewing', 'Machines take a third fewer heads and run 20% longer between them.', undefined, 'systems/settlement.ts — the crews binding'),
      p('dremm_works', 'The Works', 'Siege engines and towers deal 45% more damage.', { towerDamage: 1.45 }),
    ],
  ),
  L(
    'cogwarren_grask', 'cogwarren', 'Underwright', 'Pell Grask', 'sorcerer',
    'is the reason most of it still runs',
    'Has improved every machine in the warren at least twice, without permission, and without telling anybody which two.',
    0x8a7a4c, 0x483e22,
    ['coif', 'topknot', 'stubble', 'bronze', 'scale', 3, 'aside', -1, 'tattoo', 0.3],
    [
      p('grask_salvage', 'Better Salvage', 'Demolition and wreckage both return half again as much Scrap.', undefined, 'apply.ts — doDemolish; systems/combat.ts — the wreck path'),
      p('grask_improvised', 'Improvised', 'Buildings finish 35% faster and may be upgraded one tier further.', { buildSpeed: 1.35 }),
    ],
  ),

  // ── The Pale Litany ──────────────────────────────────────────────────────
  L(
    'litany_wren', 'litany', 'The Recorder', 'Thessaly Wren', 'sorcerer',
    'has taken the minutes since before the minutes were kept',
    'Was clerk to the court when it still met in daylight. Is the only one who noticed when that stopped, and did not raise it.',
    0x9fb8c8, 0x4d6472,
    ['crown-heavy', 'bald', 'none', 'gold', 'chasuble', 4, 'blind', 0, 'none', 0.9],
    [
      p('wren_widening', 'The Widening', 'Maximum Mana +120, and every Ossuary is worth half again as much.', { manaMax: 120 }),
      p('wren_minutes', 'The Minutes', 'Academy research costs 20% less and Souls from all sources +50%.', { soulGain: 1.5 }, 'apply.ts — doResearch, the Academy gate'),
    ],
  ),
  L(
    'litany_cadge', 'litany', 'Bone-Marshal', 'Orwin Cadge', 'warlord',
    'was buried twice and came back cross about the second one',
    'Fell at the Ninth Bell and was raised for the counter-attack. Has been waiting for the counter-attack ever since.',
    0x7b8a90, 0x3a464c,
    ['helm-horned', 'bald', 'none', 'bone', 'pauldrons', -5, 'narrow', -2, 'brand', 0.7],
    [
      p('cadge_press', 'The Standing Press', 'Maximum Mana +60, and each model standing costs 15% less of it.', { manaMax: 60 }),
      p('cadge_risen', 'Risen Again', 'A fifth of your fallen get back up, and the risen cost no Mana at all.', { corpseRise: 0.2 }),
    ],
  ),

  // ── The Moonbound ────────────────────────────────────────────────────────
  L(
    'moonbound_kavr', 'moonbound', 'Alpha', 'Kavr Longwinter', 'warlord',
    'has not slept indoors and does not intend to start',
    'Took the pack in a bad year and has kept it through eleven worse ones. Does not consider this an achievement.',
    0x8f7fc8, 0x413661,
    ['bare', 'wild', 'none', 'bone', 'pelt', -4, 'narrow', -2, 'scar', 0.45],
    [
      p('kavr_hunger', 'The Long Hunger', 'Your units deal 25% more damage and move 15% faster during a siege.', { unitDamage: 1.25, moveSpeed: 1.15 }),
      p('kavr_carried', 'Carried', 'Your storage cap is doubled — from a fifth of everyone else’s to two fifths.', undefined, 'world.ts — recomputeStorage'),
    ],
  ),
  L(
    'moonbound_ysra', 'moonbound', 'Moonsinger', 'Ysra Ashfell', 'prelate',
    'sings the pack over, and has never explained where to',
    'Learned it from something that had no mouth. Has taught it to nobody, on the grounds that they asked.',
    0x6a5c9a, 0x2f2748,
    ['antler-rack', 'braided', 'none', 'wood', 'harness', 6, 'shut', 0, 'warpaint', 0.6],
    [
      p('ysra_moonsong', 'Moonsong', 'Powers reach 40% further and cost 25% less Will.', { powerRadius: 1.4, willRegen: 1.25 }),
      p('ysra_spend', 'Spend It Tonight', 'Everything produced above the cap becomes Souls rather than nothing.', undefined, 'systems/settlement.ts — the storage clamp'),
    ],
  ),
  L(
    'moonbound_thull', 'moonbound', 'Herdfather', 'Thull Ninewinters', 'castellan',
    'keeps the herd, which he insists is not a hoard',
    'The one Moonbound who has ever built anything with a roof on it. He is very clear that it is for the animals, and the pack is very clear that it has noticed.',
    0xa08fc8, 0x4e4270,
    ['helm-horned', 'tonsure', 'short', 'lacquer', 'robe', 0, 'aside', 1, 'brand', 0.7],
    [
      p('thull_herd', 'The Herd', 'Storage is doubled — to two fifths of anybody else’s — and grain upkeep is a fifth lower.', { grainUpkeep: 0.8 }, 'world.ts — the storage multiplier'),
      p('thull_stockade', 'The Stockade', 'Walls cost what they cost anyone else and stand as long. The pack has opinions.', { wallHp: 1.6 }),
    ],
  ),

  // ── The east ─────────────────────────────────────────────────────────────
  //
  // Two apiece, which is the floor `racesMissingHouses` enforces and the number
  // a region seating rival crowns of one people actually needs. Each pair is
  // built to clear `housesDiffer` by five rather than by the three the test
  // asks for, because three is the point at which a pair stops being one
  // kingdom in two colours and is not the point at which it becomes two
  // characters.

  // ── The Long Noon ────────────────────────────────────────────────────────
  L(
    'longnoon_ashaal', 'longnoon', 'Terracewarden', 'Ashaal of the Ninth Course', 'castellan',
    'has been laying the same terrace for sixty years',
    'The ninth course of stone was begun before she woke and will be finished after she sleeps. She regards this as a normal working week.',
    0xd0a838, 0x8a6a18,
    ['circlet', 'bald', 'none', 'stone', 'harness', 2, 'narrow', 0, 'none', 0.75],
    [
      p('ashaal_course', 'The Ninth Course', 'Terraces are laid 35% faster and Hardfrost takes half as many back.', undefined, 'systems/settlement.ts — the groves binding'),
      p('ashaal_bearing', 'Load-Bearing', 'Buildings have 25% more health and your models take 10% less damage while inside your own walls.', { unitHp: 1.1 }),
    ],
  ),
  L(
    'longnoon_vekhet', 'longnoon', 'Sunspeaker', 'Vekhet Sar', 'sorcerer',
    'reads the stone and does not always report what it said',
    'Has predicted four wars, two of them accurately and one of them to the people who then started it. Declines to say which.',
    0xe8c477, 0x9a7429,
    ['mask', 'shaved-marked', 'none', 'gold', 'robe', -4, 'shut', -2, 'brand', 0.4],
    [
      p('vekhet_augury', 'Augury', 'Powers deal 30% more damage and reach a fifth further.', { powerDamage: 1.3, powerRadius: 1.2 }),
      p('vekhet_hour', 'The Hour Told', 'You see an assault forming one wave earlier than anyone else.', undefined, 'systems/director.ts — the scouting window'),
    ],
  ),
  L(
    'longnoon_iss', 'longnoon', 'Tidewatcher', 'Iss of the Drowned Course', 'huntmaster',
    'keeps the terraces the sea took, and expects them back',
    'Three courses of her hold are under water and she has never once revised the map. The sea is on a schedule too and she has read it.',
    0x6fb0a8, 0x2f6a64,
    ['crown-heavy', 'braided', 'none', 'coral', 'pauldrons', 6, 'open', 1, 'scar', 0.5],
    [
      p('iss_drowned', 'The Drowned Course', 'Terraces may be laid on a coast and carry a fifth more heads there. Ports cost half.', { popCap: 1.12 }),
      p('iss_patient', 'Reach, Not Haste', 'Ranged weapons reach 2.5 tiles further and your models are 15% harder to shift.', { archerRange: 2.5, unitHp: 1.15 }),
    ],
  ),

  // ── The Undertow ─────────────────────────────────────────────────────────
  L(
    'undertow_skell', 'undertow', 'Tallykeeper', 'Skell Nine-Ledgers', 'castellan',
    'owns the market and rather less of what is in it',
    'Keeps nine ledgers because one would be a confession. Has never been caught, audited or believed.',
    0x9c8f3a, 0x585123,
    ['coif', 'cropped', 'none', 'rag', 'robe', 3, 'aside', 1, 'none', 0.6],
    [
      p('skell_ledgers', 'Nine Ledgers', 'Every trade pays a further 18%, and demolition refunds in full rather than in part.', { resourceRate: 1.08 }, 'apply.ts — doDemolish'),
      p('skell_press', 'Room Enough', 'The press tolerates a fifth more before it turns on your buildings.', undefined, 'systems/settlement.ts — stepPress'),
    ],
  ),
  L(
    'undertow_grist', 'undertow', 'Sluicemaster', 'Grist', 'warlord',
    'opens the gate and counts what comes back',
    'Has one name, which he considers sufficient, and one method, which he considers proven. Both are the same length.',
    0xb04a2a, 0x6a2414,
    ['helm-closed', 'shaved', 'none', 'iron', 'scale', -5, 'blind', -1, 'broken-tusk', 0.5],
    [
      p('grist_sluice', 'The Sluice', 'Companies are released half again as large and arrive at full strength.', { popCap: 1.2 }, 'systems/companies.ts — the moot binding'),
      p('grist_weight', 'By Weight', 'Your models deal 25% more damage while they outnumber what is in front of them.', { unitDamage: 1.12 }),
    ],
  ),

  // ── The Thirstless ───────────────────────────────────────────────────────
  L(
    'thirstless_oroun', 'thirstless', 'Wellkeeper', 'Oroun the Unhurried', 'warlord',
    'has not been relieved and does not expect to be',
    'Was posted to the third gate of an empire that no longer exists. Is still at the third gate. The gate is also gone.',
    0xc9b48a, 0x7a6a44,
    ['helm-open', 'bald', 'none', 'stone', 'pauldrons', -2, 'narrow', -2, 'scar', 0.85],
    [
      p('oroun_post', 'The Post', 'Your models take a further 30% less damage and will not rout above a quarter strength.', { unitHp: 1.3, moraleFloor: 0.25 }),
      p('oroun_slow', 'Unhurried', 'Every blow deals 20% more and lands 10% slower. It has never mattered.', { unitDamage: 1.2, attackSpeed: 0.9 }),
    ],
  ),
  L(
    'thirstless_hesk', 'thirstless', 'Saltwright', 'Hesk of the Glass', 'huntmaster',
    'walks the sand and comes back with the road in her head',
    'Crossed the Glass Waste alone twice, which nobody disputes, and found a well in it, which everybody does.',
    0x8fb0c0, 0x3f6070,
    ['bare', 'braided', 'none', 'glass', 'pelt', 5, 'open', 0, 'tattoo', 0.45],
    [
      p('hesk_road', 'The Road Across', 'Your host does not thirst and marches a fifth faster over open ground.', { moveSpeed: 1.2 }),
      p('hesk_glass', 'Glasswork', 'Ranged weapons reach 3 tiles further and cut through a quarter of armour.', { archerRange: 3, armourPierce: 0.25 }),
    ],
  ),
  L(
    'thirstless_marrow', 'thirstless', 'Countkeeper', 'Marrow of the Ninth Kin', 'prelate',
    'knows how many are left and will not say',
    'Keeps the roll of the Thirstless. Reads it aloud once a year, entirely, and has never yet needed a second day for it, which is the part nobody asks about.',
    0xb8a0c0, 0x5a4a68,
    ['crown-heavy', 'tonsure', 'long', 'bronze', 'robe', 0, 'shut', -1, 'burn', 0.9],
    [
      p('marrow_roll', 'The Roll', 'A company raised whole costs 30% less, and the last model of a company fights as though the rest were watching.', { moraleFloor: 0.3 }),
      p('marrow_slow', 'The Ninth Kin', 'Powers reach 25% further and cool a fifth faster. There has been a great deal of time to practise.', { powerRadius: 1.25, powerCooldown: 0.8 }),
    ],
  ),
];

const BY_ID = new Map<string, Leader>();
for (const l of LEADERS) BY_ID.set(l.id, l);

export function leader(id: string): Leader | undefined {
  return BY_ID.get(id);
}

/** Every house of this people, in declaration order. The `mapsea` seam. */
export function leadersOfRace(id: string): Leader[] {
  return LEADERS.filter((l) => l.raceId === race(id).id);
}

/**
 * Which house sits on the *n*th kingdom of this race on a generated world.
 *
 * Deterministic and total: `mapsea` may seat as many kingdoms of a people as it
 * likes and will always get a leader back, wrapping round the roster if it runs
 * past the end. No RNG crosses this seam in either direction — the world
 * generator owns its own stream and this function must not perturb it.
 */
export function leaderForKingdom(raceId: string, ordinal: number): Leader {
  const list = leadersOfRace(raceId);
  const n = list.length;
  if (n === 0) return LEADERS[0];
  return list[((ordinal % n) + n) % n];
}

/** The default house for a race chosen without a kingdom behind it. */
export function defaultLeaderFor(raceId: string): Leader {
  return leaderForKingdom(raceId, 0);
}

/** Every house on the board, for the blind side-by-side the portraits want. */
export function allHouses(): HouseLook[] {
  return LEADERS.map((l) => l.look);
}

/** Sanity for the roster: every race has at least two houses to choose between. */
export function racesMissingHouses(): RaceId[] {
  return RACES.filter((r) => leadersOfRace(r.id).length < 2).map((r) => r.id);
}
